import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';

interface EmbedRouteGuardProps {
  children: React.ReactNode;
}

/**
 * EmbedRouteGuard - Restricts routes when app is embedded in an iframe
 * 
 * When ?embed=true is present in the URL, only allows:
 * - /pay (and /pay/:merchantId)
 * - /login
 * - /signin
 * - /uiapi (iframe bridge for parent postMessage queries)
 * 
 * All other routes redirect to /pay?embed=true (preserving important query params)
 */
const EmbedRouteGuard: React.FC<EmbedRouteGuardProps> = ({ children }) => {
  const location = useLocation();
  
  // Check if embed mode is enabled via query parameter
  const searchParams = new URLSearchParams(location.search);
  const isEmbedMode = searchParams.get('embed') === 'true';
  
  // If not in embed mode, allow all routes (normal app behavior)
  if (!isEmbedMode) {
    return <>{children}</>;
  }
  
  // Define allowed routes in embed mode
  const allowedRoutes = [
    '/pay',
    '/login',
    '/signin',
    '/uiapi',
  ];
  
  // Check if current path is allowed (including /pay/:merchantId pattern)
  const currentPath = location.pathname;
  const isAllowedRoute = allowedRoutes.some(route => {
    // Exact match for /login and /signin
    if (route === '/login' || route === '/signin' || route === '/uiapi') {
      return currentPath === route;
    }
    // /pay matches both /pay and /pay/:merchantId
    if (route === '/pay') {
      return currentPath === '/pay' || currentPath.startsWith('/pay/');
    }
    return false;
  });
  
  // If route is allowed, render children
  if (isAllowedRoute) {
    return <>{children}</>;
  }
  
  // Route is blocked - redirect to /pay with embed=true
  // Preserve important query parameters that might be needed
  const redirectParams = new URLSearchParams();
  redirectParams.set('embed', 'true');
  
  // Preserve important query params that might be passed to /pay
  const importantParams = ['payeeId', 'merchantId', 'amount', 'currency', 'message', 'returnUrl'];
  importantParams.forEach(param => {
    const value = searchParams.get(param);
    if (value) {
      redirectParams.set(param, value);
    }
  });
  
  const redirectPath = `/pay?${redirectParams.toString()}`;
  
  console.log(`[EmbedRouteGuard] Blocked route ${currentPath} in embed mode, redirecting to ${redirectPath}`);
  
  return <Navigate to={redirectPath} replace />;
};

export default EmbedRouteGuard;
